import { memo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Text, Truncated, useTooltip } from '@frontend/design-system';

export const PhoneTreeOptionRenderer = ({ isUnsaved = false, ...props }: PhoneTreeOptionRendererProps) => {
  return isUnsaved ? <UnsavedPhoneTreeOption {...props} /> : <SavedPhoneTreeOption {...props} />;
};

// Shared component for rendering phone tree option UI.
// Branches into SavedPhoneTreeOption or UnsavedPhoneTreeOption to avoid
// instantiating useTooltip for the common (saved) case.
interface PhoneTreeOptionRendererProps {
  name: string;
  subsequentPhoneTreeCount: number;
  isUnsaved?: boolean;
  showUnsavedTreeTooltip?: boolean;
  onViewClick: (e: React.MouseEvent) => void;
}

// No useTooltip — zero overhead for the common case
const SavedPhoneTreeOption = memo(
  ({
    name,
    subsequentPhoneTreeCount,
    onViewClick,
  }: Omit<PhoneTreeOptionRendererProps, 'isUnsaved' | 'showUnsavedTreeTooltip'>) => (
    <div css={optionContainerStyle}>
      <Button
        variant='tertiary'
        iconName='view'
        onClick={(e) => {
          e.stopPropagation();
          onViewClick(e);
        }}
      />
      <span css={optionSpanStyle}>
        <Truncated css={truncatedStyle}>{name}</Truncated>
        <PhoneTreeCountSuffix count={subsequentPhoneTreeCount} />
      </span>
    </div>
  )
);
SavedPhoneTreeOption.displayName = 'SavedPhoneTreeOption';

// useTooltip only instantiated for unsaved trees, triggerProps stays on the outer span
const UnsavedPhoneTreeOption = memo(
  ({
    name,
    subsequentPhoneTreeCount,
    showUnsavedTreeTooltip = true,
    onViewClick,
  }: Omit<PhoneTreeOptionRendererProps, 'isUnsaved'>) => {
    const { t } = useTranslation('phone');
    const { Tooltip: UnsavedTreeTooltip, tooltipProps, triggerProps } = useTooltip({ showArrow: false });

    return (
      <div css={optionContainerStyle}>
        <Button
          variant='tertiary'
          iconName='view'
          onClick={(e) => {
            e.stopPropagation();
            onViewClick(e);
          }}
        />
        <span css={optionSpanStyle} {...triggerProps}>
          <Truncated css={truncatedStyle}>{name}</Truncated>
          <PhoneTreeCountSuffix count={subsequentPhoneTreeCount} />
          <Text as='span' color='subdued' css={unsavedTextStyle}>
            {t('(Unsaved)')}
          </Text>
          {showUnsavedTreeTooltip && (
            <UnsavedTreeTooltip {...tooltipProps}>
              <Text color='white'>{t('Save this Call Route to use this Phone Tree.')}</Text>
            </UnsavedTreeTooltip>
          )}
        </span>
      </div>
    );
  }
);
UnsavedPhoneTreeOption.displayName = 'UnsavedPhoneTreeOption';

// Shared component for rendering subsequent tree count suffix
const PhoneTreeCountSuffix = ({ count }: { count: number }) => {
  const { t } = useTranslation('phone');

  if (count === 0) return null;

  return (
    <Text as='span' color='subdued' css={countSuffixTextStyle}>
      {t(`+ {{count}} subsequent tree`, { count })}
    </Text>
  );
};

const countSuffixTextStyle = css`
  padding-left: ${theme.spacing(0.5)};
`;

const optionContainerStyle = css`
  display: flex;
  align-items: center;
`;

const optionSpanStyle = css`
  width: 89%;
  display: flex;
`;

const truncatedStyle = css`
  width: fit-content;
`;

const unsavedTextStyle = css`
  padding-left: ${theme.spacing(0.5)};
  font-style: italic;
`;
